/**
 * Module-level debounced row cost estimator for OrderForm (Rule 11 safe).
 * Prevents firing N HTTP calls per keystroke while typing quantities (e.g. 22.500)
 * and skips redundant setFieldValue calls when unitCost has not changed (FB-9JS6IR).
 */

export interface OrderCostRowLike {
  productId?: string | null;
  quantity?: number | null;
  costPrice?: number | null;
}

export interface CreateDebouncedRowCostEstimatorOptions<
  TRow extends OrderCostRowLike,
> {
  debounceMs?: number;
  estimateCost: (
    productId: string,
    quantity: number,
  ) => Promise<{ success?: boolean; data?: { unitCost?: number } } | null | undefined>;
  getRows: () => TRow[];
  applyRowCost: (rowIndex: number, nextUnitCost: number) => void;
}

export function createDebouncedRowCostEstimator<TRow extends OrderCostRowLike>({
  debounceMs = 500,
  estimateCost,
  getRows,
  applyRowCost,
}: CreateDebouncedRowCostEstimatorOptions<TRow>) {
  const timers = new Map<number, ReturnType<typeof setTimeout>>();
  const seqMap = new Map<number, number>();
  let disposed = false;

  const clearRowTimer = (rowIndex: number) => {
    const existing = timers.get(rowIndex);
    if (existing !== undefined) {
      clearTimeout(existing);
      timers.delete(rowIndex);
    }
  };

  const scheduleChangedRows = (prevRows: TRow[], nextRows: TRow[]) => {
    if (disposed) return;

    if (prevRows.length > nextRows.length) {
      for (let idx = nextRows.length; idx < prevRows.length; idx++) {
        clearRowTimer(idx);
        seqMap.set(idx, (seqMap.get(idx) || 0) + 1);
      }
    }

    nextRows.forEach((nextRow, rowIndex) => {
      const prevRow = prevRows[rowIndex];
      const nextProductId = String(nextRow?.productId || "");
      const prevProductId = String(prevRow?.productId || "");
      const nextQty = Number(nextRow?.quantity || 0);
      const prevQty = Number(prevRow?.quantity || 0);

      const hasChanged =
        !prevRow || nextProductId !== prevProductId || nextQty !== prevQty;
      if (!hasChanged) return;

      clearRowTimer(rowIndex);
      const nextSeq = (seqMap.get(rowIndex) || 0) + 1;
      seqMap.set(rowIndex, nextSeq);

      if (!nextProductId || !(nextQty > 0)) {
        return;
      }

      const timer = setTimeout(() => {
        timers.delete(rowIndex);
        if (disposed || seqMap.get(rowIndex) !== nextSeq) return;

        estimateCost(nextProductId, nextQty)
          .then((res) => {
            if (disposed || seqMap.get(rowIndex) !== nextSeq) return;
            const nextUnitCost = Number(res?.data?.unitCost ?? 0);
            if (!res?.success || !(nextUnitCost > 0)) return;

            const currentRows = getRows() || [];
            const liveRow = currentRows[rowIndex];
            if (!liveRow) return;
            if (
              String(liveRow.productId || "") !== nextProductId ||
              Number(liveRow.quantity || 0) !== nextQty
            ) {
              return;
            }
            if (Number(liveRow.costPrice || 0) === nextUnitCost) {
              return;
            }

            applyRowCost(rowIndex, nextUnitCost);
          })
          .catch((err) => {
            console.error("Estimate cost failed", err);
          });
      }, debounceMs);

      timers.set(rowIndex, timer);
    });
  };

  const dispose = () => {
    disposed = true;
    for (const timer of timers.values()) {
      clearTimeout(timer);
    }
    timers.clear();
  };

  return {
    scheduleChangedRows,
    dispose,
  };
}
